import React from 'react'
import { Box, Chip, Stack, Typography, alpha } from '@mui/material'
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded'
import FlightRoundedIcon from '@mui/icons-material/FlightRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'

import PoweredByWrapper from '../poweredBy'

interface PackageSummaryFact {
    key: string
    icon: 'duration' | 'airline' | 'dates'
    value: string
}

interface PackageSummaryProps {
    destinationName: string
    tagline?: string
    /** Ribbon from the CMS, e.g. "Best seller". */
    badge?: string
    tourPackageLabel: string
    facts: PackageSummaryFact[]
    tags: string[]
    isMobile: boolean
}

const ICONS = {
    duration: ScheduleRoundedIcon,
    airline: FlightRoundedIcon,
    dates: CalendarTodayRoundedIcon
}

const PackageSummary: React.FC<PackageSummaryProps> = ({
    destinationName,
    tagline,
    badge,
    tourPackageLabel,
    facts,
    tags,
    isMobile
}) => {
    return (
        <Stack spacing={1.5}>
            <Box>
                {/* The supplier's attribution belongs with the package name, not buried in the
                    form, so it reads as the source of the trip rather than of the enquiry. */}
                <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ alignItems: 'center', justifyContent: 'space-between', mb: badge ? 1 : 0 }}
                >
                    {badge ? (
                        <Chip
                            icon={<StarRoundedIcon />}
                            label={badge}
                            size="small"
                            sx={theme => ({
                                height: 26,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                '& .MuiChip-icon': { fontSize: 16, color: 'primary.main' }
                            })}
                        />
                    ) : (
                        <Typography
                            sx={{
                                fontSize: '0.688rem',
                                fontWeight: 700,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                color: 'primary.main'
                            }}
                        >
                            {tourPackageLabel}
                        </Typography>
                    )}
                </Stack>
                <Typography
                    component="h1"
                    sx={{
                        fontSize: isMobile ? '1.75rem' : '2.25rem',
                        fontWeight: 700,
                        lineHeight: 1.15,
                        color: 'textColors.primary',
                        mt: 0.5
                    }}
                >
                    {destinationName}
                </Typography>
                {tagline && (
                    <Typography
                        sx={{
                            fontSize: isMobile ? '0.875rem' : '1rem',
                            color: 'textColors.secondary',
                            mt: 0.75
                        }}
                    >
                        {tagline}
                    </Typography>
                )}
            </Box>

            {facts.length > 0 && (
                <Stack
                    direction="row"
                    sx={{
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: isMobile ? 1.5 : 2.5,
                        // The supplier's attribution closes the facts line, as in the design.
                        '& > :last-of-type': { marginInlineStart: isMobile ? 0 : 'auto' }
                    }}
                >
                    {facts.map(fact => {
                        const Icon = ICONS[fact.icon]
                        return (
                            <Stack key={fact.key} direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                                <Icon sx={{ fontSize: 16, color: 'textColors.subheading' }} />
                                <Typography
                                    sx={{
                                        fontSize: isMobile ? '1rem' : '0.875rem',
                                        fontWeight: 600,
                                        color: 'textColors.subtitle'
                                    }}
                                >
                                    {fact.value}
                                </Typography>
                            </Stack>
                        )
                    })}
                    {!isMobile && <PoweredByWrapper height={18} />}
                </Stack>
            )}

            {tags.length > 0 && (
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                    {tags.map(tag => (
                        <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                                height: 26,
                                fontSize: '0.75rem',
                                bgcolor: 'neutral.100',
                                color: 'textColors.subtitle',
                                border: '1px solid',
                                borderColor: 'borders.default'
                            }}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    )
}

export { PackageSummary }
export type { PackageSummaryProps, PackageSummaryFact }
export default PackageSummary
